import type { LLMConfig, LLMMessage } from '@/IsekaiRPG/modules/llm/llmTypes';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import type { STPromptCompilerTrace } from './llmSTPromptCompiler';
import type { MessagePipelineChainTrace } from './llmMessagePipeline';

export class LLMTracingService {
  private static TAG = 'LLMProvider';

  static logGenerateStart(
    requestId: string,
    provider: LLMConfig['provider'],
    generationType: string,
    streaming: boolean,
  ): void {
    LogService.info(this.TAG, `[${requestId}] ${streaming ? '开始流式调用' : '开始调用'}`, {
      provider,
      generationType,
    });
  }

  static logGenerateDone(
    requestId: string,
    metrics: { compileMs: number; totalMs: number; outputLength: number },
    streaming: boolean,
  ): void {
    LogService.info(this.TAG, `[${requestId}] ${streaming ? '流式调用完成' : '调用完成'}`, metrics);
  }

  static logGenerateError(requestId: string, error: unknown, streaming: boolean): void {
    LogService.error(this.TAG, `[${requestId}] ${streaming ? '流式请求失败' : '请求失败'}`, error);
  }

  static logPromptMessages(stage: string, messages: LLMMessage[], requestId?: string): void {
    const summary = messages.map((message, index) => ({
      index,
      role: message.role,
      contentPreview: this.truncateText(message.content, 180),
      contentLength: message.content.length,
    }));

    const messageText = requestId
      ? `[${requestId}] 提示词消息 ${stage}: ${messages.length} 条`
      : `提示词消息 ${stage}: ${messages.length} 条`;
    LogService.info(this.TAG, messageText, summary);
  }

  static logPromptCompileTrace(trace: STPromptCompilerTrace, requestId?: string): void {
    const matchedEntries = trace.entries.filter(entry => entry.matched);
    const skippedEntries = trace.entries.filter(entry => !entry.matched);

    const summaryMessage = requestId ? `[${requestId}] 提示词编译追踪摘要` : '提示词编译追踪摘要';
    LogService.info(this.TAG, summaryMessage, {
      generationType: trace.generationType,
      worldBookEnabled: trace.worldBookEnabled,
      matchedCount: trace.matchedCount,
      totalEntries: trace.totalEntries,
      skippedCount: skippedEntries.length,
    });

    if (matchedEntries.length > 0) {
      LogService.info(
        this.TAG,
        `${requestId ? `[${requestId}] ` : ''}世界书命中明细: ${matchedEntries.length} 条`,
        matchedEntries.map(entry => ({
          id: entry.id,
          name: entry.name,
          round: entry.round,
          reason: entry.reason,
          mode: entry.mode,
          depth: entry.depth,
          order: entry.order,
        })),
      );
    }

    if (skippedEntries.length > 0) {
      LogService.info(
        this.TAG,
        `${requestId ? `[${requestId}] ` : ''}世界书未命中明细: ${skippedEntries.length} 条`,
        skippedEntries.map(entry => ({
          id: entry.id,
          name: entry.name,
          reason: entry.reason,
        })),
      );
    }
  }

  static logStreamingDowngrade(
    requestId: string,
    payload: { provider: LLMConfig['provider']; requestedStream: boolean; mode: 'normal' | 'stream' },
  ): void {
    LogService.warn(this.TAG, `[${requestId}] 当前调用路径不支持流式处理，已自动降级为非流式`, payload);
  }

  static logMessagePipelineTrace(stage: string, trace: MessagePipelineChainTrace, requestId?: string): void {
    const detailMessage = requestId ? `[${requestId}] 消息链路追踪全文 ${stage}` : `消息链路追踪全文 ${stage}`;
    LogService.info(this.TAG, detailMessage, this.buildDebugTracePayload(trace));
  }

  private static truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.slice(0, maxLength)}...(truncated)`;
  }

  /**
   * 调试全文去重视图：
   * - 只保留一份核心消息列表（outboundMessages）
   * - systemEntries 仅在 useSystemPrompt=true 时单独保留
   */
  private static buildDebugTracePayload(trace: MessagePipelineChainTrace): Record<string, unknown> {
    const outboundMessages = trace.useSystemPrompt ? trace.nonSystemMessages : trace.normalizedMessages;
    return {
      sourceKind: trace.sourceKind,
      useSystemPrompt: trace.useSystemPrompt,
      summary: trace.summary,
      systemEntries: trace.useSystemPrompt ? trace.systemEntries : [],
      outboundMessages,
    };
  }
}
